import api from '@/lib/api';
import {
  ExerciseReadDTO,
  ExerciseSearchResult,
} from '@/types/exercise';

export interface PaginatedSearchResponse {
  results: Array<{
    exercise: ExerciseReadDTO;
    similarity_score: number;
  }>;
  next_cursor: string | null;
  has_more: boolean;
}

const API_BASE_URL = import.meta.env.VITE_API_URL;

class ExerciseApi {
  private baseURL: string;

  constructor() {
    this.baseURL = `${API_BASE_URL}/api/v1/exercises`;
  }

  /**
   * Get all exercises with optional filtering
   */
  async getExercises(filters?: {
    muscle_group?: string;
    type?: string;
  }): Promise<ExerciseReadDTO[]> {
    const response = await api.get<ExerciseReadDTO[]>(this.baseURL, {
      params: filters,
    });
    return response.data;
  }

  /**
   * Search for exercises by keyword.
   * Returns exercises sorted by similarity score in descending order.
   */
  async searchExercises(keyword: string, topN: number = 10): Promise<ExerciseSearchResult[]> {
    const response = await api.get<[ExerciseReadDTO, number][]>(`${this.baseURL}/search`, {
      params: { keyword: keyword.trim(), top_n: topN },
    });
    // Transform tuple response to ExerciseSearchResult
    return response.data.map(([exercise, similarity_score]) => ({
      exercise,
      similarity_score,
    }));
  }

  /**
   * Get a specific exercise by ID
   */
  async getExercise(exerciseId: string): Promise<ExerciseReadDTO> {
    const response = await api.get<ExerciseReadDTO>(`${this.baseURL}/${exerciseId}`);
    return response.data;
  }

  /**
   * Get exercises by muscle group
   */
  async getExercisesByMuscleGroup(muscleGroup: string): Promise<ExerciseReadDTO[]> {
    return this.getExercises({ muscle_group: muscleGroup });
  }

  /**
   * Get exercises by type
   */
  async getExercisesByType(type: string): Promise<ExerciseReadDTO[]> {
    return this.getExercises({ type });
  }

  /**
   * Search exercises with pagination support.
   * Returns exercises sorted alphabetically when no keyword is provided.
   * Uses cursor-based pagination for efficient large dataset handling.
   */
  async searchExercisesPaginated(
    keyword?: string,
    limit: number = 10,
    cursor?: string | null
  ): Promise<PaginatedSearchResponse> {
    const params: { keyword?: string; limit: number; cursor?: string } = { limit };
    if (keyword && keyword.trim()) {
      params.keyword = keyword.trim();
    }
    if (cursor) {
      params.cursor = cursor;
    }
    const response = await api.get<PaginatedSearchResponse>(`${this.baseURL}/search/paginated`, {
      params,
    });
    return response.data;
  }
}

export const exerciseApi = new ExerciseApi();

